import { useState } from 'react'
import { useGetAllCoursesQuery } from '@/features/admin/adminApi'
import { formatDate } from '@/lib/formatDate'
import { courseDisplayName, type NamedCourse } from '@/lib/localizedText'
import { useLanguage } from '@/hooks/useLanguage'
import type { Lang } from '@/types/lang'
import { AdminPagination } from './AdminPagination'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const LIMIT = 20

// Находка 9: раньше было курсивное «Без названия» (регресс при переходе на
// courseDisplayName потерял его в пользу литерального дефолта 'Untitled').
// courseDisplayName по-прежнему даёт симметричный ru/en фолбэк — заглушку
// подставляем локально, только когда оба слота пусты.
const courseNameCell = (course: NamedCourse, language: Lang, untitled: string) =>
  !course.name?.trim() && !course.nameEn?.trim() ? (
    <span className="text-muted-foreground italic">{untitled}</span>
  ) : (
    courseDisplayName(course, language)
  )

export const AdminCoursesTable = () => {
  const [page, setPage] = useState(1)
  const { language } = useLanguage()
  const m = useMessages(messages)
  const { data, isLoading } = useGetAllCoursesQuery({ page, limit: LIMIT })

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">{m.loading}</div>
  }

  if (!data || data.items.length === 0) {
    return <div className="text-sm text-muted-foreground py-8 text-center">{m.noCourses}</div>
  }

  return (
    <div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colName}
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colAuthors}
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colStatus}
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colUpdated}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((course) => (
              <tr
                key={course.id}
                className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-3 font-medium">
                  {courseNameCell(course, language, m.untitled)}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {course.authors.map((a) => a.username).join(', ') || '—'}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex flex-wrap items-center gap-1">
                    {course.isPublished ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">
                        {m.published}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {m.draft}
                      </span>
                    )}
                    {course.isPublic === false ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600">
                        {m.linkOnly}
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{formatDate(course.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AdminPagination page={data.page} pages={data.pages} onPageChange={setPage} />
    </div>
  )
}
